import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box, Button, Tab, Tabs, Chip,
  Typography, TextField, MenuItem, Tooltip,
} from '@mui/material';
import AddIcon           from '@mui/icons-material/Add';
import LabelImportantIcon from '@mui/icons-material/LabelImportant';
// Icônes disponibles dans le sélecteur
import CheckCircleIcon      from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon   from '@mui/icons-material/HourglassEmpty';
import PlayArrowIcon         from '@mui/icons-material/PlayArrow';
import LocalShippingIcon     from '@mui/icons-material/LocalShipping';
import DoneAllIcon           from '@mui/icons-material/DoneAll';
import CancelIcon            from '@mui/icons-material/Cancel';
import PauseCircleIcon       from '@mui/icons-material/PauseCircle';
import ErrorOutlineIcon      from '@mui/icons-material/ErrorOutline';
import ReceiptIcon           from '@mui/icons-material/Receipt';
import PaidIcon              from '@mui/icons-material/Paid';
import CreditCardOffIcon     from '@mui/icons-material/CreditCardOff';
import InventoryIcon         from '@mui/icons-material/Inventory';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import BuildIcon             from '@mui/icons-material/Build';
import StarIcon              from '@mui/icons-material/Star';

import { AppDispatch } from '../reduxStore/Store';
import { WorkflowStatut, WorkflowStatutTransition } from '../models/models';
import { TableHeader, TableAction } from '../constants/interfaces';
import { ButtonColor, ButtonType } from '../constants/enums';
import MyTable from '../components/MyTable';
import { useDialog } from '../components/DialogComponent';
import { useSnackbar } from '../components/SnackbarProvider';
import AppPage from '../components/layout/AppPage';
import AppSurface from '../components/layout/AppSurface';
import AppPageHeader from '../components/layout/AppPageHeader';
import { getLabels } from '../constants/fieldLabels';
import { fetchSysTypeDocuments } from '../reduxStore/SysTypeDocumentSlice';
import {
  fetchWorkflowStatuts,
  fetchWorkflowStatutTransitions,
  saveWorkflowStatut,
  deleteWorkflowStatut,
  saveWorkflowStatutTransition,
  deleteWorkflowStatutTransition,
  setSelectedStatut,
  setSelectedTransition,
  clearWorkflowStatutData,
  clearMessages,
} from '../reduxStore/WorkflowStatutSlice';

// ── Palette de couleurs prédéfinies ────────────────────────────────────────

const COLOR_PALETTE = [
  { hex: '#9E9E9E', label: 'Gris' },
  { hex: '#2196F3', label: 'Bleu' },
  { hex: '#03A9F4', label: 'Bleu clair' },
  { hex: '#00BCD4', label: 'Cyan' },
  { hex: '#4CAF50', label: 'Vert' },
  { hex: '#8BC34A', label: 'Vert clair' },
  { hex: '#FF9800', label: 'Orange' },
  { hex: '#FF5722', label: 'Orange foncé' },
  { hex: '#F44336', label: 'Rouge' },
  { hex: '#E91E63', label: 'Rose' },
  { hex: '#9C27B0', label: 'Violet' },
  { hex: '#673AB7', label: 'Indigo' },
  { hex: '#3F51B5', label: 'Bleu marine' },
  { hex: '#009688', label: 'Teal' },
  { hex: '#607D8B', label: 'Bleu gris' },
  { hex: '#795548', label: 'Marron' },
  { hex: '#FFC107', label: 'Ambre' },
  { hex: '#FFEB3B', label: 'Jaune' },
  { hex: '#212121', label: 'Noir' },
  { hex: '#FFFFFF', label: 'Blanc' },
];

// ── Icônes disponibles ─────────────────────────────────────────────────────

const ICON_OPTIONS: { value: string; label: string; icon: React.ReactNode }[] = [
  { value: '',                    label: '— Aucune —',        icon: null },
  { value: 'HourglassEmpty',      label: 'En attente',        icon: <HourglassEmptyIcon fontSize="small" /> },
  { value: 'PlayArrow',           label: 'En cours',          icon: <PlayArrowIcon fontSize="small" /> },
  { value: 'LocalShipping',       label: 'Expédié',           icon: <LocalShippingIcon fontSize="small" /> },
  { value: 'CheckCircle',         label: 'Terminé',           icon: <CheckCircleIcon fontSize="small" /> },
  { value: 'DoneAll',             label: 'Tout validé',       icon: <DoneAllIcon fontSize="small" /> },
  { value: 'Cancel',              label: 'Annulé',            icon: <CancelIcon fontSize="small" /> },
  { value: 'PauseCircle',         label: 'En pause',          icon: <PauseCircleIcon fontSize="small" /> },
  { value: 'ErrorOutline',        label: 'Bloqué',            icon: <ErrorOutlineIcon fontSize="small" /> },
  { value: 'Receipt',             label: 'Facturé',           icon: <ReceiptIcon fontSize="small" /> },
  { value: 'Paid',                label: 'Payé',              icon: <PaidIcon fontSize="small" /> },
  { value: 'CreditCardOff',       label: 'Non payé',          icon: <CreditCardOffIcon fontSize="small" /> },
  { value: 'Inventory',           label: 'Stock',             icon: <InventoryIcon fontSize="small" /> },
  { value: 'AssignmentTurnedIn',  label: 'Validé',            icon: <AssignmentTurnedInIcon fontSize="small" /> },
  { value: 'Build',               label: 'En traitement',     icon: <BuildIcon fontSize="small" /> },
  { value: 'Star',                label: 'Important',         icon: <StarIcon fontSize="small" /> },
];

// ── Composant sélecteur de couleur ─────────────────────────────────────────

interface ColorPickerProps {
  value: string;
  onChange: (hex: string) => void;
}

const ColorPicker: React.FC<ColorPickerProps> = ({ value, onChange }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
      Couleur
    </Typography>
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
      {COLOR_PALETTE.map(c => (
        <Tooltip key={c.hex} title={c.label} placement="top">
          <Box
            onClick={() => onChange(c.hex)}
            sx={{
              width: 28, height: 28,
              borderRadius: '6px',
              bgcolor: c.hex,
              cursor: 'pointer',
              border: value === c.hex
                ? '3px solid #1976d2'
                : '2px solid rgba(0,0,0,0.15)',
              boxShadow: value === c.hex ? '0 0 0 2px rgba(25,118,210,0.3)' : 'none',
              transition: 'transform 0.1s',
              '&:hover': { transform: 'scale(1.2)' },
            }}
          />
        </Tooltip>
      ))}
    </Box>
    {/* Aperçu + hex libre */}
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
      <Box sx={{ width: 28, height: 28, borderRadius: '6px', bgcolor: value, border: '1px solid rgba(0,0,0,0.2)', flexShrink: 0 }} />
      <TextField
        size="small"
        label="Hex personnalisé"
        value={value}
        onChange={e => onChange(e.target.value)}
        sx={{ flexGrow: 1 }}
        slotProps={{ htmlInput: { maxLength: 7, style: { fontFamily: 'monospace' } } }}
      />
    </Box>
  </Box>
);

// ── Formulaire inline statut ───────────────────────────────────────────────

interface StatutFormProps {
  initial?: WorkflowStatut | null;
  docTypes: { code: string; libelle: string }[];
  onSave: (s: WorkflowStatut) => void;
  onCancel: () => void;
}

const StatutForm: React.FC<StatutFormProps> = ({ initial, docTypes, onSave, onCancel }) => {
  const [form, setForm] = useState<WorkflowStatut>({
    code: '', libelle: '', couleur: '#9E9E9E', icone: '',
    ordre: 0, is_initial: false, is_final: false,
    doc_type_code: null, departement_id: null, visible: true,
    ...initial,
  });

  const set = (field: keyof WorkflowStatut, value: any) =>
    setForm(f => ({ ...f, [field]: value }));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 380 }}>
      <TextField
        size="small" label="Code" value={form.code} required
        onChange={e => set('code', e.target.value.toUpperCase())}
      />
      <TextField
        size="small" label="Libellé" value={form.libelle} required
        onChange={e => set('libelle', e.target.value)}
      />

      {/* Type de document */}
      <TextField
        select size="small" label="Type de document"
        value={form.doc_type_code ?? ''}
        onChange={e => set('doc_type_code', e.target.value || null)}
      >
        <MenuItem value="">— Tous types (générique) —</MenuItem>
        {docTypes.map(d => (
          <MenuItem key={d.code} value={d.code}>
            {d.libelle} <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>({d.code})</Typography>
          </MenuItem>
        ))}
      </TextField>

      {/* Icône */}
      <TextField
        select size="small" label="Icône"
        value={form.icone ?? ''}
        onChange={e => set('icone', e.target.value || '')}
      >
        {ICON_OPTIONS.map(opt => (
          <MenuItem key={opt.value} value={opt.value}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {opt.icon && <Box sx={{ display: 'flex', alignItems: 'center', color: form.couleur ?? '#9E9E9E' }}>{opt.icon}</Box>}
              {opt.label}
            </Box>
          </MenuItem>
        ))}
      </TextField>

      {/* Couleur */}
      <ColorPicker value={form.couleur ?? '#9E9E9E'} onChange={v => set('couleur', v)} />

      <TextField
        size="small" label="Ordre" type="number" value={form.ordre}
        onChange={e => set('ordre', Number(e.target.value))}
      />
      <TextField
        select size="small" label="Statut initial"
        value={form.is_initial ? '1' : '0'}
        onChange={e => set('is_initial', e.target.value === '1')}
      >
        <MenuItem value="0">Non</MenuItem>
        <MenuItem value="1">Oui</MenuItem>
      </TextField>
      <TextField
        select size="small" label="Statut final"
        value={form.is_final ? '1' : '0'}
        onChange={e => set('is_final', e.target.value === '1')}
      >
        <MenuItem value="0">Non</MenuItem>
        <MenuItem value="1">Oui</MenuItem>
      </TextField>

      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        <Button variant="outlined" onClick={onCancel}>Annuler</Button>
        <Button variant="contained" onClick={() => onSave(form)}>Enregistrer</Button>
      </Box>
    </Box>
  );
};

// ── Formulaire inline transition ───────────────────────────────────────────

interface TransitionFormProps {
  initial?: WorkflowStatutTransition | null;
  statuts: WorkflowStatut[];
  docTypes: { code: string; libelle: string }[];
  onSave: (t: WorkflowStatutTransition) => void;
  onCancel: () => void;
}

const TRIGGERS = ['DISPATCH_PARTIEL', 'DISPATCH_COMPLET', 'DOC_FINAL_CREE', 'PAIEMENT_PARTIEL', 'PAIEMENT_COMPLET'];

const TransitionForm: React.FC<TransitionFormProps> = ({ initial, statuts, docTypes, onSave, onCancel }) => {
  const [form, setForm] = useState<WorkflowStatutTransition>({
    doc_type_code: null, statut_source_id: null, statut_cible_id: 0,
    libelle_action: null, auto_trigger: null,
    ...initial,
  });
  const set = (field: keyof WorkflowStatutTransition, value: any) =>
    setForm(f => ({ ...f, [field]: value }));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 400 }}>
      {/* Type de document */}
      <TextField
        select size="small" label="Type de document"
        value={form.doc_type_code ?? ''}
        onChange={e => set('doc_type_code', e.target.value || null)}
      >
        <MenuItem value="">— Tous types —</MenuItem>
        {docTypes.map(d => (
          <MenuItem key={d.code} value={d.code}>
            {d.libelle} <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>({d.code})</Typography>
          </MenuItem>
        ))}
      </TextField>

      <TextField
        select size="small" label="Statut source (vide = tous)"
        value={form.statut_source_id ?? ''}
        onChange={e => set('statut_source_id', e.target.value || null)}
      >
        <MenuItem value="">— Tous les statuts —</MenuItem>
        {statuts.map(s => (
          <MenuItem key={s.id} value={s.id}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: s.couleur ?? '#9E9E9E', flexShrink: 0 }} />
              {s.libelle}
              <Typography component="span" variant="caption" color="text.secondary">({s.code})</Typography>
            </Box>
          </MenuItem>
        ))}
      </TextField>

      <TextField
        select size="small" label="Statut cible *"
        value={form.statut_cible_id ?? ''}
        onChange={e => set('statut_cible_id', Number(e.target.value))}
      >
        {statuts.map(s => (
          <MenuItem key={s.id} value={s.id}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: s.couleur ?? '#9E9E9E', flexShrink: 0 }} />
              {s.libelle}
              <Typography component="span" variant="caption" color="text.secondary">({s.code})</Typography>
            </Box>
          </MenuItem>
        ))}
      </TextField>

      <TextField
        select size="small" label="Déclencheur automatique"
        value={form.auto_trigger ?? ''}
        onChange={e => set('auto_trigger', e.target.value || null)}
      >
        <MenuItem value="">— Transition manuelle —</MenuItem>
        {TRIGGERS.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
      </TextField>

      <TextField
        size="small" label="Libellé bouton (manuel)"
        value={form.libelle_action ?? ''}
        onChange={e => set('libelle_action', e.target.value || null)}
      />

      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        <Button variant="outlined" onClick={onCancel}>Annuler</Button>
        <Button variant="contained" onClick={() => onSave(form)} disabled={!form.statut_cible_id}>
          Enregistrer
        </Button>
      </Box>
    </Box>
  );
};

// ── Utilitaire : couleur claire ou foncée ? ────────────────────────────────

function isLightColor(hex: string): boolean {
  const h = hex.replace('#', '');
  if (h.length !== 6) return true;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return r * 0.299 + g * 0.587 + b * 0.114 > 155;
}

// ── Composant visualisation du cycle ──────────────────────────────────────

const SVG_W = 900;
const SVG_H = 540;
const CX = SVG_W / 2;
const CY = SVG_H / 2 + 10;
const NODE_R = 40;

function getCircularPositions(n: number): { x: number; y: number }[] {
  if (n === 0) return [];
  if (n === 1) return [{ x: CX, y: CY }];
  const R = Math.min(220, Math.max(140, n * 30));
  return Array.from({ length: n }, (_, i) => {
    const angle = (i * 2 * Math.PI) / n - Math.PI / 2;
    return { x: CX + R * Math.cos(angle), y: CY + R * Math.sin(angle) };
  });
}

interface CycleViewProps {
  statuts: WorkflowStatut[];
  transitions: WorkflowStatutTransition[];
  docTypeOptions: { code: string; libelle: string }[];
}

const WorkflowCycleView: React.FC<CycleViewProps> = ({ statuts, transitions, docTypeOptions }) => {
  const [filterDocType, setFilterDocType] = useState('');

  // Statuts visibles : correspondant au type filtré OU génériques (null)
  const visibleStatuts = statuts.filter(s =>
    !filterDocType || !s.doc_type_code || s.doc_type_code === filterDocType
  ).sort((a, b) => (a.ordre ?? 0) - (b.ordre ?? 0));

  // Transitions visibles : type filtré OU génériques, avec source/cible dans visibleStatuts
  const visibleIds = new Set(visibleStatuts.map(s => s.id));
  const visibleTransitions = transitions.filter(t => {
    const typeOk = !filterDocType || !t.doc_type_code || t.doc_type_code === filterDocType;
    const tgtOk  = t.statut_cible_id && visibleIds.has(Number(t.statut_cible_id));
    return typeOk && tgtOk;
  });

  const positions = getCircularPositions(visibleStatuts.length);
  const nodeMap = new Map<number, { x: number; y: number }>();
  visibleStatuts.forEach((s, i) => { if (s.id) nodeMap.set(s.id, positions[i]); });

  // Pour détecter les paires bidirectionnelles → décaler la courbe
  const seenPairs = new Map<string, number>();

  const edges = visibleTransitions.map((t, idx) => {
    const tgt = t.statut_cible_id ? nodeMap.get(Number(t.statut_cible_id)) : null;
    if (!tgt) return null;
    const src = t.statut_source_id ? nodeMap.get(Number(t.statut_source_id)) : null;

    const fwdKey = `${t.statut_source_id ?? 'any'}-${t.statut_cible_id}`;
    const revKey = `${t.statut_cible_id}-${t.statut_source_id ?? 'any'}`;
    const curveOffset = seenPairs.has(revKey) ? 45 : 0;
    seenPairs.set(fwdKey, idx);

    return { t, src, tgt, curveOffset };
  }).filter(Boolean) as { t: WorkflowStatutTransition; src: { x: number; y: number } | null; tgt: { x: number; y: number }; curveOffset: number }[];

  return (
    <Box sx={{ mt: 2 }}>
      {/* En-tête de la section */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 2, mb: 2,
        flexWrap: 'wrap',
      }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ flexShrink: 0 }}>
          Cycle de statuts
        </Typography>

        <TextField
          select size="small" label="Type de document" sx={{ minWidth: 220 }}
          value={filterDocType}
          onChange={e => setFilterDocType(e.target.value)}
        >
          <MenuItem value="">— Tous (génériques) —</MenuItem>
          {docTypeOptions.map(d => (
            <MenuItem key={d.code} value={d.code}>{d.libelle}</MenuItem>
          ))}
        </TextField>

        {/* Légende */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, ml: 'auto', flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <svg width={32} height={12}>
              <defs>
                <marker id="lgBlue" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
                  <path d="M0,0 L0,6 L6,3 z" fill="#1976d2" />
                </marker>
              </defs>
              <line x1="2" y1="6" x2="26" y2="6" stroke="#1976d2" strokeWidth="2" markerEnd="url(#lgBlue)" />
            </svg>
            <Typography variant="caption">Manuelle</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <svg width={32} height={12}>
              <defs>
                <marker id="lgOrange" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
                  <path d="M0,0 L0,6 L6,3 z" fill="#ed6c02" />
                </marker>
              </defs>
              <line x1="2" y1="6" x2="26" y2="6" stroke="#ed6c02" strokeWidth="2" strokeDasharray="5,3" markerEnd="url(#lgOrange)" />
            </svg>
            <Typography variant="caption">Auto-déclenchée</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <svg width={22} height={22}>
              <circle cx="11" cy="11" r="8" fill="#1976d2" opacity={0.15} stroke="#1976d2" strokeWidth="1.5" strokeDasharray="3,2" />
            </svg>
            <Typography variant="caption">Initial</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <svg width={22} height={22}>
              <circle cx="11" cy="11" r="9" fill="none" stroke="#2e7d32" strokeWidth="2.5" />
              <circle cx="11" cy="11" r="5" fill="#2e7d32" opacity={0.3} />
            </svg>
            <Typography variant="caption">Final</Typography>
          </Box>
        </Box>
      </Box>

      {/* Diagramme SVG */}
      <Box sx={{
        border: '1px solid', borderColor: 'divider',
        borderRadius: 2, bgcolor: 'background.default', overflow: 'hidden',
      }}>
        {visibleStatuts.length === 0 ? (
          <Box sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
            <Typography>Aucun statut à afficher pour ce filtre</Typography>
          </Box>
        ) : (
          <svg width="100%" viewBox={`0 0 ${SVG_W} ${SVG_H}`} style={{ display: 'block' }}>
            <defs>
              <marker id="cwBlue"   markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
                <path d="M0,0 L0,6 L8,3 z" fill="#1976d2" />
              </marker>
              <marker id="cwOrange" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
                <path d="M0,0 L0,6 L8,3 z" fill="#ed6c02" />
              </marker>
              <marker id="cwGray"   markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
                <path d="M0,0 L0,6 L8,3 z" fill="#888" />
              </marker>
            </defs>

            {/* ── Flèches ── */}
            {edges.map((edge, i) => {
              const { t, src, tgt, curveOffset } = edge;
              const isAuto   = !!t.auto_trigger;
              const strokeC  = src ? (isAuto ? '#ed6c02' : '#1976d2') : '#888';
              const markerId = src ? (isAuto ? 'cwOrange' : 'cwBlue') : 'cwGray';
              const label    = t.auto_trigger ?? t.libelle_action ?? '';

              let d = '';
              let labelX = 0, labelY = 0;

              if (!src) {
                // "depuis n'importe quel statut" → flèche tombante du haut du nœud cible
                const ax = tgt.x;
                const ay = tgt.y - NODE_R - 50;
                d = `M ${ax},${ay} L ${ax},${tgt.y - NODE_R - 4}`;
                labelX = ax + 8;
                labelY = ay + 18;
              } else if (src.x === tgt.x && src.y === tgt.y) {
                // Self-loop
                const angle = Math.atan2(src.y - CY, src.x - CX);
                const cpX   = src.x + Math.cos(angle) * (NODE_R + 55);
                const cpY   = src.y + Math.sin(angle) * (NODE_R + 55);
                const sx1   = src.x + Math.cos(angle - 0.45) * NODE_R;
                const sy1   = src.y + Math.sin(angle - 0.45) * NODE_R;
                const ex1   = src.x + Math.cos(angle + 0.45) * NODE_R;
                const ey1   = src.y + Math.sin(angle + 0.45) * NODE_R;
                d = `M ${sx1},${sy1} C ${cpX - 20},${cpY - 20} ${cpX + 20},${cpY + 20} ${ex1},${ey1}`;
                labelX = cpX + 6;
                labelY = cpY;
              } else {
                // Flèche courbe entre deux nœuds distincts
                const dx  = tgt.x - src.x;
                const dy  = tgt.y - src.y;
                const len = Math.sqrt(dx * dx + dy * dy) || 1;
                const nx  = dx / len, ny = dy / len;
                const px  = -ny,      py = nx;

                const x1 = src.x + nx * NODE_R;
                const y1 = src.y + ny * NODE_R;
                const x2 = tgt.x - nx * (NODE_R + 6);
                const y2 = tgt.y - ny * (NODE_R + 6);
                const mx  = (x1 + x2) / 2 + px * curveOffset;
                const my  = (y1 + y2) / 2 + py * curveOffset;
                d = `M ${x1},${y1} Q ${mx},${my} ${x2},${y2}`;
                labelX = mx + px * 10;
                labelY = my + py * 10 - 4;
              }

              return (
                <g key={i}>
                  <path
                    d={d} fill="none"
                    stroke={strokeC} strokeWidth={1.8}
                    strokeDasharray={isAuto ? '6,3' : undefined}
                    markerEnd={`url(#${markerId})`}
                    opacity={0.85}
                  />
                  {label && (
                    <text
                      x={labelX} y={labelY}
                      textAnchor="middle"
                      fontSize={9} fontFamily="monospace"
                      fill={strokeC}
                      style={{ userSelect: 'none' }}
                    >
                      {label.length > 20 ? label.slice(0, 18) + '…' : label}
                    </text>
                  )}
                  {/* Indicateur "∀" pour les transitions sans source */}
                  {!src && (
                    <text
                      x={tgt.x} y={tgt.y - NODE_R - 56}
                      textAnchor="middle"
                      fontSize={11} fill="#888"
                      style={{ userSelect: 'none' }}
                    >
                      ∀
                    </text>
                  )}
                </g>
              );
            })}

            {/* ── Nœuds ── */}
            {visibleStatuts.map((s, i) => {
              const pos   = positions[i];
              const color = s.couleur ?? '#9E9E9E';
              const tColor = isLightColor(color) ? '#111' : '#fff';

              const labelLines: string[] = [];
              const words = s.libelle.split(' ');
              let line = '';
              words.forEach(w => {
                if ((line + ' ' + w).trim().length <= 11) {
                  line = (line + ' ' + w).trim();
                } else {
                  if (line) labelLines.push(line);
                  line = w.slice(0, 11);
                }
              });
              if (line) labelLines.push(line);
              const maxLines = 2;
              const displayLines = labelLines.slice(0, maxLines);
              if (labelLines.length > maxLines) displayLines[maxLines - 1] += '…';

              return (
                <g key={s.id} transform={`translate(${pos.x},${pos.y})`}>
                  {/* Anneau Initial */}
                  {s.is_initial && (
                    <circle
                      r={NODE_R + 8}
                      fill="rgba(25,118,210,0.08)"
                      stroke="#1976d2"
                      strokeWidth={1.5}
                      strokeDasharray="4,2"
                    />
                  )}
                  {/* Anneau Final */}
                  {s.is_final && (
                    <circle
                      r={NODE_R + (s.is_initial ? 5 : 6)}
                      fill="none"
                      stroke="#2e7d32"
                      strokeWidth={2.5}
                    />
                  )}
                  {/* Cercle principal */}
                  <circle r={NODE_R} fill={color} stroke="rgba(255,255,255,0.6)" strokeWidth={2} />

                  {/* Libellé (2 lignes max) */}
                  {displayLines.map((ln, li) => (
                    <text
                      key={li}
                      textAnchor="middle"
                      dominantBaseline="middle"
                      fontSize={11} fontWeight={600} fill={tColor}
                      y={(displayLines.length === 1 ? -2 : -8 + li * 13)}
                      style={{ userSelect: 'none' }}
                    >
                      {ln}
                    </text>
                  ))}
                  {/* Code en dessous */}
                  <text
                    textAnchor="middle" dominantBaseline="middle"
                    fontSize={9} fill={tColor} opacity={0.75}
                    y={displayLines.length === 1 ? 11 : 16}
                    style={{ userSelect: 'none' }}
                  >
                    {s.code}
                  </text>

                  {/* Badges Initial / Final sous le nœud */}
                  {s.is_initial && (
                    <text textAnchor="middle" fontSize={8} fill="#1976d2"
                      y={NODE_R + 18} style={{ userSelect: 'none' }}>
                      INITIAL
                    </text>
                  )}
                  {s.is_final && (
                    <text textAnchor="middle" fontSize={8} fill="#2e7d32"
                      y={NODE_R + (s.is_initial ? 28 : 18)} style={{ userSelect: 'none' }}>
                      FINAL
                    </text>
                  )}
                </g>
              );
            })}
          </svg>
        )}
      </Box>
    </Box>
  );
};

// ── Page principale ────────────────────────────────────────────────────────

const GestionWorkflowStatuts: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { showSnackbar } = useSnackbar();

  const {
    statutsData, transitionsData,
    total, totalTransitions,
    error, successMessage,
  } = useSelector((state: any) => state.WorkflowStatutSlice);

  const { sysTypeData } = useSelector((state: any) => state.SysTypeDocumentSlice);

  const [tab, setTab] = useState(0);

  // ── Filtres serveur ──────────────────────────────────────────────────────
  const [fetchStatuts, setFetchStatuts] = useState<{
    doc_type_code?: string | null; limit: number; offset: number;
  }>({ limit: 20, offset: 0 });

  const [fetchTransitions, setFetchTransitions] = useState<{
    doc_type_code?: string | null; limit: number; offset: number;
  }>({ limit: 20, offset: 0 });

  // ── Filtres client (appliqués après reception) ───────────────────────────
  const [filterStatut, setFilterStatut] = useState<{
    is_initial: '' | 'true' | 'false';
    is_final:   '' | 'true' | 'false';
  }>({ is_initial: '', is_final: '' });

  const [filterTransition, setFilterTransition] = useState<{
    auto_trigger: string;
  }>({ auto_trigger: '' });

  // ── Dialogs ──────────────────────────────────────────────────────────────
  const [StatutDialog,     openStatutDialog,     closeStatutDialog]     = useDialog({ modal: true });
  const [TransitionDialog, openTransitionDialog, closeTransitionDialog] = useDialog({ modal: true });
  const [ConfirmDialog,    openConfirmDialog,    closeConfirmDialog]    = useDialog({ modal: true });

  const [confirmContent,  setConfirmContent]  = useState<React.ReactNode>(null);
  const [editStatut,      setEditStatut]      = useState<WorkflowStatut | null>(null);
  const [editTransition,  setEditTransition]  = useState<WorkflowStatutTransition | null>(null);

  useEffect(() => {
    dispatch(fetchSysTypeDocuments());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchWorkflowStatuts({ ...fetchStatuts }));
    return () => {
      dispatch(clearWorkflowStatutData());
      dispatch(setSelectedStatut(null));
    };
  }, [dispatch, fetchStatuts, successMessage]);

  useEffect(() => {
    dispatch(fetchWorkflowStatutTransitions({ ...fetchTransitions }));
  }, [dispatch, fetchTransitions, successMessage]);

  useEffect(() => {
    if (successMessage) { showSnackbar(successMessage, 'success'); dispatch(clearMessages()); }
    if (error)          { showSnackbar(error, 'error');            dispatch(clearMessages()); }
  }, [successMessage, error]);

  // Libellés courts pour les selects
  const docTypeOptions: { code: string; libelle: string }[] =
    (sysTypeData ?? []).map((d: any) => ({ code: d.code, libelle: d.libelle }));

  // ── Données filtrées côté client ─────────────────────────────────────────
  const statutsFiltered = (statutsData ?? []).filter((s: WorkflowStatut) => {
    if (filterStatut.is_initial === 'true'  && !s.is_initial) return false;
    if (filterStatut.is_initial === 'false' &&  s.is_initial) return false;
    if (filterStatut.is_final   === 'true'  && !s.is_final)   return false;
    if (filterStatut.is_final   === 'false' &&  s.is_final)   return false;
    return true;
  });

  const transitionsFiltered = (transitionsData ?? []).filter((t: WorkflowStatutTransition) => {
    if (!filterTransition.auto_trigger) return true;
    if (filterTransition.auto_trigger === '__manual__') return !t.auto_trigger;
    return t.auto_trigger === filterTransition.auto_trigger;
  });

  // ── Handlers statuts ────────────────────────────────────────────────────

  const handleSaveStatut = async (statut: WorkflowStatut) => {
    await dispatch(saveWorkflowStatut({ ...statut }));
    closeStatutDialog();
  };

  const handleDeleteStatut = (statut: WorkflowStatut) => {
    if (!statut.id) return;
    setConfirmContent(
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>Supprimer ce statut ?</Typography>
        <Typography sx={{ mb: 2 }}>
          Le statut <strong>{statut.libelle}</strong> sera supprimé définitivement.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Button variant="outlined" onClick={closeConfirmDialog}>Annuler</Button>
          <Button variant="contained" color="error" onClick={async () => {
            await dispatch(deleteWorkflowStatut(statut.id!));
            closeConfirmDialog();
          }}>Supprimer</Button>
        </Box>
      </Box>
    );
    openConfirmDialog();
  };

  const openEditStatut = (statut?: WorkflowStatut) => {
    setEditStatut(statut ?? null);
    dispatch(setSelectedStatut(statut ?? null));
    openStatutDialog();
  };

  // ── Handlers transitions ────────────────────────────────────────────────

  const handleSaveTransition = async (transition: WorkflowStatutTransition) => {
    await dispatch(saveWorkflowStatutTransition({ ...transition }));
    closeTransitionDialog();
  };

  const handleDeleteTransition = (t: WorkflowStatutTransition) => {
    if (!t.id) return;
    setConfirmContent(
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>Supprimer cette transition ?</Typography>
        <Typography sx={{ mb: 2 }}>La transition sera supprimée définitivement.</Typography>
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Button variant="outlined" onClick={closeConfirmDialog}>Annuler</Button>
          <Button variant="contained" color="error" onClick={async () => {
            await dispatch(deleteWorkflowStatutTransition(t.id!));
            closeConfirmDialog();
          }}>Supprimer</Button>
        </Box>
      </Box>
    );
    openConfirmDialog();
  };

  const openEditTransition = (transition?: WorkflowStatutTransition) => {
    setEditTransition(transition ?? null);
    dispatch(setSelectedTransition(transition ?? null));
    openTransitionDialog();
  };

  // ── Colonnes statuts ────────────────────────────────────────────────────

  const headersStatuts: TableHeader<WorkflowStatut>[] = [
    { key: 'code',    label: 'Code' },
    { key: 'libelle', label: 'Libellé',
      render: (row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: row.couleur ?? '#ccc', flexShrink: 0 }} />
          {row.libelle}
        </Box>
      ),
    },
    { key: 'doc_type_code', label: 'Type doc',
      render: (row) => {
        if (!row.doc_type_code) return <Typography variant="caption" color="text.secondary">Tous</Typography>;
        const found = docTypeOptions.find(d => d.code === row.doc_type_code);
        return <span>{found ? found.libelle : row.doc_type_code}</span>;
      },
    },
    { key: 'is_initial', label: 'Initial',
      render: (row) => row.is_initial ? <Chip label="Oui" size="small" color="info" /> : null,
    },
    { key: 'is_final', label: 'Final',
      render: (row) => row.is_final ? <Chip label="Oui" size="small" color="success" /> : null,
    },
  ];

  const actionsStatuts: TableAction<WorkflowStatut>[] = [
    { label: 'Modifier',  type: ButtonType.EDIT,   callback: openEditStatut },
    { label: 'Supprimer', type: ButtonType.DELETE,  callback: handleDeleteStatut, color: ButtonColor.ERROR },
  ];

  // ── Colonnes transitions ────────────────────────────────────────────────

  const headersTransitions: TableHeader<WorkflowStatutTransition>[] = [
    { key: 'doc_type_code', label: 'Type doc',
      render: (row) => {
        if (!row.doc_type_code) return <Typography variant="caption" color="text.secondary">Tous</Typography>;
        const found = docTypeOptions.find(d => d.code === row.doc_type_code);
        return <span>{found ? found.libelle : row.doc_type_code}</span>;
      },
    },
    { key: 'statut_source_libelle', label: 'De',
      render: (row) => row.statut_source_libelle
        ? <Chip label={row.statut_source_libelle} size="small" sx={{ bgcolor: row.statut_source_couleur ?? '#9E9E9E', color: '#fff' }} />
        : <Typography variant="caption" color="text.secondary">Tous</Typography>,
    },
    { key: 'statut_cible_libelle', label: 'Vers',
      render: (row) => (
        <Chip label={row.statut_cible_libelle ?? '—'} size="small" sx={{ bgcolor: row.statut_cible_couleur ?? '#9E9E9E', color: '#fff' }} />
      ),
    },
    { key: 'auto_trigger',   label: 'Déclencheur' },
    { key: 'libelle_action', label: 'Bouton' },
  ];

  const actionsTransitions: TableAction<WorkflowStatutTransition>[] = [
    { label: 'Modifier',  type: ButtonType.EDIT,   callback: openEditTransition },
    { label: 'Supprimer', type: ButtonType.DELETE,  callback: handleDeleteTransition, color: ButtonColor.ERROR },
  ];

  return (
    <AppPage maxWidth={false}>
      <AppPageHeader title="Statuts de documents" icon={<LabelImportantIcon />} />

      {/* Dialog statut */}
      <StatutDialog>
        <Box sx={{ p: 2, minWidth: 420 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {editStatut ? 'Modifier le statut' : 'Nouveau statut'}
          </Typography>
          <StatutForm
            initial={editStatut}
            docTypes={docTypeOptions}
            onSave={handleSaveStatut}
            onCancel={closeStatutDialog}
          />
        </Box>
      </StatutDialog>

      {/* Dialog transition */}
      <TransitionDialog>
        <Box sx={{ p: 2, minWidth: 440 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {editTransition ? 'Modifier la transition' : 'Nouvelle transition'}
          </Typography>
          <TransitionForm
            initial={editTransition}
            statuts={statutsData}
            docTypes={docTypeOptions}
            onSave={handleSaveTransition}
            onCancel={closeTransitionDialog}
          />
        </Box>
      </TransitionDialog>

      {/* Dialog confirmation suppression */}
      <ConfirmDialog>
        {confirmContent}
      </ConfirmDialog>

      <AppSurface>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
          <Tab label="Statuts" />
          <Tab label="Transitions" />
        </Tabs>

        {tab === 0 && (
          <>
            {/* ── Filtres statuts ── */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2, alignItems: 'center' }}>
              <TextField
                select size="small" label="Type de document" sx={{ minWidth: 200 }}
                value={fetchStatuts.doc_type_code ?? ''}
                onChange={e => setFetchStatuts(p => ({ ...p, doc_type_code: e.target.value || null, offset: 0 }))}
              >
                <MenuItem value="">— Tous types —</MenuItem>
                {docTypeOptions.map(d => (
                  <MenuItem key={d.code} value={d.code}>{d.libelle}</MenuItem>
                ))}
              </TextField>
              <TextField
                select size="small" label="Initial" sx={{ minWidth: 120 }}
                value={filterStatut.is_initial}
                onChange={e => setFilterStatut(p => ({ ...p, is_initial: e.target.value as any }))}
              >
                <MenuItem value="">Tous</MenuItem>
                <MenuItem value="true">Oui</MenuItem>
                <MenuItem value="false">Non</MenuItem>
              </TextField>
              <TextField
                select size="small" label="Final" sx={{ minWidth: 120 }}
                value={filterStatut.is_final}
                onChange={e => setFilterStatut(p => ({ ...p, is_final: e.target.value as any }))}
              >
                <MenuItem value="">Tous</MenuItem>
                <MenuItem value="true">Oui</MenuItem>
                <MenuItem value="false">Non</MenuItem>
              </TextField>
              <Box sx={{ flexGrow: 1 }} />
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => openEditStatut()}>
                Nouveau statut
              </Button>
            </Box>
            <MyTable
              headers={headersStatuts}
              data={statutsFiltered}
              actions={actionsStatuts}
              totalRows={total}
              onPageChange={(limit, offset) => setFetchStatuts(p => ({ ...p, limit, offset }))}
              showDetails
              fieldLabels={getLabels('WorkflowStatut')}
            />
          </>
        )}

        {tab === 1 && (
          <>
            {/* ── Filtres transitions ── */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2, alignItems: 'center' }}>
              <TextField
                select size="small" label="Type de document" sx={{ minWidth: 200 }}
                value={fetchTransitions.doc_type_code ?? ''}
                onChange={e => setFetchTransitions(p => ({ ...p, doc_type_code: e.target.value || null, offset: 0 }))}
              >
                <MenuItem value="">— Tous types —</MenuItem>
                {docTypeOptions.map(d => (
                  <MenuItem key={d.code} value={d.code}>{d.libelle}</MenuItem>
                ))}
              </TextField>
              <TextField
                select size="small" label="Déclencheur" sx={{ minWidth: 200 }}
                value={filterTransition.auto_trigger}
                onChange={e => setFilterTransition(p => ({ ...p, auto_trigger: e.target.value }))}
              >
                <MenuItem value="">— Tous —</MenuItem>
                <MenuItem value="__manual__">Manuel (bouton)</MenuItem>
                {TRIGGERS.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
              <Box sx={{ flexGrow: 1 }} />
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => openEditTransition()}>
                Nouvelle transition
              </Button>
            </Box>
            <MyTable
              headers={headersTransitions}
              data={transitionsFiltered}
              actions={actionsTransitions}
              totalRows={totalTransitions}
              onPageChange={(limit, offset) => setFetchTransitions(p => ({ ...p, limit, offset }))}
              showDetails
              fieldLabels={getLabels('WorkflowStatutTransition')}
            />
          </>
        )}
      </AppSurface>

      {/* ── Vue cycle illustratif ── */}
      <AppSurface sx={{ mt: 2 }}>
        <WorkflowCycleView
          statuts={statutsData ?? []}
          transitions={transitionsData ?? []}
          docTypeOptions={docTypeOptions}
        />
      </AppSurface>
    </AppPage>
  );
};

export default GestionWorkflowStatuts;
